import { useApi } from '@/composables/use-api.ts';
import { acceptHMRUpdate, defineStore } from 'pinia';
import {
  AttendanceAction,
  AttendanceActivityDto,
  AttendanceLocation,
} from '@/services/api/attendance-activity-api.ts';
import { AttendanceSummaryDto } from '@/services/api/attendance-day-api.ts';

interface State {
  isLoading: boolean;
  summary: AttendanceSummaryDto | undefined;
}

const noData = { result: null, error: null };

export const useActivityStore = defineStore('activity', {
  state: (): State => {
    return {
      isLoading: false,
      summary: undefined,
    };
  },

  getters: {
    isEnded: (state) => state.summary?.latest?.action === 'WORK_END',

    isAway: (state) => state.summary?.latest?.action === 'AWAY_START',

    isWorking: (state) =>
      state.summary?.latest?.action === 'WORK_START' ||
      state.summary?.latest?.action === 'AWAY_END',

    canStartToday: (state) => !state.summary?.latest,

    lastAction: (state) => state.summary?.latest?.action,

    attendanceId: (state) => state.summary?.id,
  },

  actions: {
    async updateSummary() {
      const { attendance } = useApi();
      return (this.summary = await attendance.day.getSummaryAsync());
    },

    async startWorkAsync(location: AttendanceLocation = 'INSIDE') {
      if (this.isLoading) return noData;

      this.isLoading = true;
      const { attendance } = useApi();
      const result = await attendance.activity.startWorkAsync(location);
      if (result?.status === 'CREATED') {
        if (!this.summary) await this.updateSummary();
        this.summary!.latest = result;
        this.summary!.id = result.attendanceId;
      }
      this.isLoading = false;
      return result;
    },

    async changeWorkStatusAsync(
      status: AttendanceAction,
      location: AttendanceLocation = 'INSIDE',
      comment?: string
    ) {
      if (this.isLoading) return noData;

      this.isLoading = true;
      const { attendance } = useApi();
      let task: Promise<AttendanceActivityDto>;

      if (status === 'AWAY_START') {
        task = attendance.activity.startAwayAsync(this.attendanceId!);
      } else if (status === 'AWAY_END') {
        task = attendance.activity.endAwayAsync(this.attendanceId!, location);
      } else if (status === 'WORK_END') {
        task = attendance.activity.endWorkAsync(
          this.attendanceId!,
          comment
        );
      } else if (status === 'WORK_START') {
        task = attendance.activity.restartWorkAsync(
          this.attendanceId!,
          location
        );
      } else {
        throw new Error(`잘못된 근태 설정 : ${status}`);
      }

      const result = await task;
      if (result?.attendanceId)
        await this.updateSummary();
      
      this.isLoading = false;
      return result;
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useActivityStore, import.meta.hot));
}
